import { themeVar } from '@/styles/theme';
import { RefObject, useEffect, useState } from 'react';
import styled from 'styled-components';

import { SortIcon } from '@/assets/icons/sortIcon';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { useResponsive } from '@/hooks/use-responsive';
import { Tooltip as MuiTooltip } from '@mui/material';
import { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import CheckBox from '../basicComponents/checkbox';
import { SIZE_DROPDOWN_ACTIONS } from './constants';
import { getTooltipForSort } from './sortCategoryRegistry';
import { ISort, SORT_ORDER, TableColumns } from './types';

type CheckBox = {
  checked: boolean;
  onChange: (isChecked?: boolean) => void;
  show?: boolean;
};

interface TableHeaderProps {
  showActionButton?: boolean;
  columns: TableColumns[];
  withOutSort?: boolean;
  refetch: any;
  savedSort?: ISort;
  checkBox?: CheckBox;
  className?: string;
  withHorizontalScroll?: boolean;
  /** Ref for fixed column wrapper – parent sets transform via ref for smooth scroll sync (use with useStickyTableHeaderWithHorizontalScroll) */
  fixedColumnRef?: RefObject<HTMLDivElement | null>;
  /** Optional prop to force recalculation */
  forceRecalculateTableFiltersHeight?: any;
  savedSetSort?: (value: ISort) => void;
}

const TableHeader = ({
  refetch,
  columns,
  showActionButton = false,
  withOutSort = false,
  savedSort,
  checkBox,
  className,
  withHorizontalScroll = false,
  fixedColumnRef,
  forceRecalculateTableFiltersHeight,
  savedSetSort
}: TableHeaderProps) => {
  const { isTablet, isDesktop, isLargeDesktop } = useResponsive();
  const [tableFiltersHeight, setTableFiltersHeight] = useState(0);
  const [sort, setSort] = useState<ISort>(savedSort);

  const WIDTH = 100 / columns.length;

  useEffect(() => {
    const updateTableFiltersHeight = () => {
      const filterPageElement = document.getElementById('table-filters');
      if (filterPageElement) {
        const height = filterPageElement.offsetHeight || 0;
        setTableFiltersHeight(height);
      }
    };

    // Execute immediately
    updateTableFiltersHeight();

    // Execute again after one second
    setTimeout(updateTableFiltersHeight, 1000);
  }, [isTablet, isDesktop, isLargeDesktop, forceRecalculateTableFiltersHeight]);

  const getToolTipTitle = (keySort: string) => {
    return getTooltipForSort(keySort, sort?.asc ?? true);
  };

  const handleSort = (key: string, order: boolean) => {
    setSort({
      key,
      asc: order
    });
    refetch({
      order: order ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      sort: key
    });
    if (savedSetSort) {
      savedSetSort({
        key,
        asc: order
      });
    }
  };

  const renderSort = (column: TableColumns) => {
    if (!column?.title || !column?.keySort || !column?.key || withOutSort) return null;
    const isSorted = column.key === sort?.key;

    return (
      <StyledTooltip placement="bottom" title={getToolTipTitle(column?.keySort)} arrow>
        <SortBtn onClick={() => handleSort(column.key!, !sort?.asc)} isRotate={isSorted && sort?.asc} isActive={isSorted}>
          <SortIcon
            style={{
              marginBottom: -3,
              marginTop: isSorted && sort?.asc ? -2 : 0
            }}
            fill={themeVar('header.action')}
          />
        </SortBtn>
      </StyledTooltip>
    );
  };

  const renderColumn = (column: TableColumns, withScroll: boolean) => {
    const ColumnWrapper = column?.isFixed ? FixedTableColumnItem : TableColumnItem;

    return (
      <ColumnWrapper key={column.key || column?.keySort} width={column?.width ?? WIDTH} withHorizontalScroll={withScroll}>
        <TableColumnTitle
          id="tableColumnTitle"
          withOutSort={withOutSort || !column.key}
          onClick={column.key && column?.keySort && !withOutSort ? () => handleSort(column.key!, !sort?.asc) : () => null}
        >
          {column.title}
        </TableColumnTitle>
        {renderSort(column)}
        {column?.tooltip && (
          <Tooltip title={column.tooltip} maxContentWidth={300}>
            <InfoCircleIcon fill={themeVar('header.action')} style={{ position: 'relative', top: '3px' }} />
          </Tooltip>
        )}
      </ColumnWrapper>
    );
  };

  if (withHorizontalScroll) {
    const fixedColumns = columns.filter((col) => col.isFixed && !col.isHidden);
    const scrollableColumns = columns.filter((col) => !col.isFixed && !col.isHidden);
    const fixedWidth = fixedColumns.reduce((acc, col) => acc + (col.width ?? 0), 0);

    return (
      <TableHeaderWithHorizontalScrollWrap className={className}>
        {checkBox?.show && (
          <CheckboxWrapper>
            <CheckBox id="input" isActive={checkBox.checked} onClick={checkBox.onChange} />
          </CheckboxWrapper>
        )}

        <FixedTableColumnsWrapper ref={fixedColumnRef} width={fixedWidth}>
          {fixedColumns.map((col) => renderColumn(col, withHorizontalScroll))}
        </FixedTableColumnsWrapper>

        <ScrollableColumnsWrapper>{scrollableColumns.map((col) => renderColumn(col, withHorizontalScroll))}</ScrollableColumnsWrapper>

        {showActionButton && <ActionButtonSpace />}
      </TableHeaderWithHorizontalScrollWrap>
    );
  }

  return (
    <TableHeaderWrap id="table-header" tableFiltersHeight={tableFiltersHeight} isTablet={isTablet} className={className}>
      {checkBox?.show && (
        <CheckboxWrapper>
          <CheckBox id="input" isActive={checkBox.checked} onClick={checkBox.onChange} />
        </CheckboxWrapper>
      )}

      {columns.map((col) => renderColumn(col, withHorizontalScroll))}

      {showActionButton && <ActionButtonSpace />}
    </TableHeaderWrap>
  );
};

const TableHeaderWrap = styled.div<{ tableFiltersHeight: number; isTablet: boolean }>`
  position: sticky;
  top: ${({ tableFiltersHeight, isTablet }) => (isTablet ? `${50 + tableFiltersHeight}px` : `${tableFiltersHeight}px`)};
  z-index: 8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-items: stretch;
  width: 100%;
  min-height: 70px;
  background-color: ${themeVar('header.base')};
  border-radius: 8px;
  padding: 0 24px;
  gap: 5px;
`;

const TableHeaderWithHorizontalScrollWrap = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-items: stretch;
  width: 100%;
  min-height: 70px;
  background-color: ${themeVar('header.base')};
  border-radius: 8px;
  padding: 0;
  gap: 0;
`;

const FixedTableColumnsWrapper = styled.div<{ width: number }>`
  position: sticky;
  left: 0;
  z-index: 9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: ${({ width }) => `${width}px`};
  width: 100%;
  padding: 0 24px;
  margin: 10px 0;
  border-radius: 8px 0 0 8px;
  border-right: 1px solid ${themeVar('border.base')};
  background-color: ${themeVar('header.base')};
`;

export const SortBtn = styled.div<{ isActive: boolean; isRotate: boolean; addAdaptive?: boolean }>`
  display: ${({ isActive }) => (isActive ? 'flex' : 'none')};
  transform: ${({ isRotate }) => (isRotate ? 'rotate(180deg)' : 'rotate(0deg)')};
  cursor: pointer;
  justify-content: center;
  align-items: center;
  width: ${({ addAdaptive }) => (addAdaptive ? 20 : 24)}px;
  min-width: ${({ addAdaptive }) => (addAdaptive ? 20 : 24)}px;
  height: ${({ addAdaptive }) => (addAdaptive ? 20 : 24)}px;
  border-radius: 50%;
  background-color: ${({ isActive, theme }) => (isActive ? themeVar('menu.active') : 'transparent')};
`;

const FixedTableColumnItem = styled.div<{ width: number }>`
  max-width: ${({ width }) => `${width}px`};
  width: 100%;
  background-color: ${themeVar('header.base')};
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover ${SortBtn} {
    display: flex;
  }
`;

const ScrollableColumnsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0 24px;
`;

const TableColumnItem = styled.div<{ width: number; withHorizontalScroll: boolean }>`
  width: ${({ width, withHorizontalScroll }) => (withHorizontalScroll ? `${width}px` : `${width}%`)};
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover ${SortBtn} {
    display: flex;
  }
`;

const TableColumnTitle = styled.span<{ withOutSort?: boolean }>`
  color: ${themeVar('header.action')};
  font-family: 'Blinker', serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 22.4px;
  text-transform: uppercase;
  align-items: flex-start;
  cursor: ${({ withOutSort }) => (withOutSort ? 'default' : 'pointer')};
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
`;

const ActionButtonSpace = styled.div`
  width: ${SIZE_DROPDOWN_ACTIONS}px;
`;

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />)`
  & .${tooltipClasses.tooltip} {
    opacity: 1;
    display: flex;
    align-items: center;
    padding: 20px;
    font-size: 12px;
    font-family: 'Inter';
    font-weight: 400;
    height: 20px;
  }

  & .${tooltipClasses.arrow} {
    // color: '#C7CBD2',
  }
`;

export default TableHeader;
